import "reflect-metadata";
import Container from "typedi";
import { ContentScriptChannel } from "./ContentScriptChannel";
import { ContentScriptRegistry } from "./ContentScriptRegistry";
import { ControllerBroadcaster } from "./ControllerBroadcaster";
import { InstanceCoordinator } from "./InstanceCoordinator";
import { LeaderElection } from "./LeaderElection";
import { AgentConnector } from "./AgentConnector";
import { StudentRulesCache } from "./state/StudentRulesCache";
import { UrlEvaluator } from "./state/UrlEvaluator";
import { AnnouncementState } from "./state/AnnouncementState";
import { DeviceStatusCache } from "./state/DeviceStatusCache";
import { Announce } from "./commands/Announce";
import { SetStudentRules } from "./commands/SetStudentRules";
import { SetStudentDeviceStatus } from "./commands/SetStudentDeviceStatus";
import { GetDesktopSnapshot } from "./commands/GetDesktopSnapshot";
import { PushTab } from "./commands/PushTab";
import { UrlWebCategorizationChanged } from "./commands/UrlWebCategorizationChanged";
import { GetDeviceIpAddresses } from "./commands/GetDeviceIpAddresses";
import { GetDeviceLocation } from "./commands/GetDeviceLocation";
import { GetDeviceInformation } from "./commands/GetDeviceInformation";
import { NotSupportedCommands } from "./commands/NotSupportedCommands";
import { ContentScriptOutboundMessage, ControllerOutboundMessage } from "../../shared/ChannelProtocol";
import type { InstanceBroadcastMessage } from "./InstanceCoordinator";

function setStatus(text: string): void {
    const el = document.getElementById("status");
    if (el) el.textContent = `Zaindari Controller (${text})`;
}

function registerCommands(): void {
    Container.get(Announce);
    Container.get(SetStudentRules);
    Container.get(SetStudentDeviceStatus);
    Container.get(GetDesktopSnapshot);
    Container.get(PushTab);
    Container.get(UrlWebCategorizationChanged);
    Container.get(GetDeviceIpAddresses);
    Container.get(GetDeviceLocation);
    Container.get(GetDeviceInformation);
    Container.get(NotSupportedCommands);
}

/**
 * Wires the cross-instance bus so non-leader controllers forward their
 * content-script events to the leader, and the leader pushes outbound
 * messages back to the right instance.
 */
function bindCrossInstance(
    coordinator: InstanceCoordinator,
    leaderElection: LeaderElection,
    broadcaster: ControllerBroadcaster,
    contentScriptChannel: ContentScriptChannel,
    registry: ContentScriptRegistry,
    urlEvaluator: UrlEvaluator
): void {
    // Every controller tells its own content script about snapshot requests.
    coordinator.onMessage((msg: InstanceBroadcastMessage) => {
        if (msg.kind === "to-content-script") {
            if (msg.targetInstanceId === coordinator.instanceId) {
                contentScriptChannel.send(msg.message);
            }
        } else if (msg.kind === "broadcast-to-content-scripts") {
            contentScriptChannel.send(msg.message);
        } else if (msg.kind === "request-snapshot-report") {
            const state = registry.getState();
            if (!state) return;
            coordinator.broadcast({
                kind: "snapshot-report",
                requestId: msg.requestId,
                instanceId: coordinator.instanceId,
                tabId: state.tabId,
                url: state.url,
                title: state.title,
                favicon: state.favicon,
                active: state.active
            });
        }
    });

    // Non-leaders forward their content-script events to whoever is leader.
    registry.onEvent(async (event: ContentScriptOutboundMessage) => {
        if (!leaderElection.getIsLeader()) {
            coordinator.broadcast({ kind: "content-script-event", instanceId: coordinator.instanceId, event });
            return;
        }

        // Leader handles locally.
        if (event.kind === "register" || event.kind === "url-change") {
            const result = await urlEvaluator.evaluate(event.url);
            const message: ControllerOutboundMessage = {
                kind: "decision",
                action: result.action,
                ...(result.data ? { data: result.data } : {})
            };
            broadcaster.sendToInstance(coordinator.instanceId, message);
        }
    });

    // Leader receives events forwarded from other instances.
    coordinator.onMessage(async (msg: InstanceBroadcastMessage) => {
        if (!leaderElection.getIsLeader()) return;
        if (msg.kind !== "content-script-event") return;

        const event = msg.event;
        if (event.kind === "register" || event.kind === "url-change") {
            const result = await urlEvaluator.evaluate(event.url);
            const message: ControllerOutboundMessage = {
                kind: "decision",
                action: result.action,
                ...(result.data ? { data: result.data } : {})
            };
            broadcaster.sendToInstance(msg.instanceId, message);
        }
    });
}

async function bootstrap(): Promise<void> {
    setStatus("booting");

    registerCommands();

    const coordinator = Container.get(InstanceCoordinator);
    const leaderElection = Container.get(LeaderElection);
    const broadcaster = Container.get(ControllerBroadcaster);
    const registry = Container.get(ContentScriptRegistry);
    const urlEvaluator = Container.get(UrlEvaluator);
    const rulesCache = Container.get(StudentRulesCache);
    const deviceStatusCache = Container.get(DeviceStatusCache);
    const announcementState = Container.get(AnnouncementState);

    coordinator.start();
    leaderElection.start();

    const contentScriptChannel = new ContentScriptChannel();
    registry.attach(contentScriptChannel);
    broadcaster.setLocalChannel(contentScriptChannel);

    bindCrossInstance(coordinator, leaderElection, broadcaster, contentScriptChannel, registry, urlEvaluator);

    // Announcements: when the leader's AnnouncementState changes, broadcast.
    announcementState.onChange((current) => {
        if (!leaderElection.getIsLeader()) return;
        if (current) {
            broadcaster.broadcastToContentScripts({ kind: "show-announcement", data: current });
        } else {
            broadcaster.broadcastToContentScripts({ kind: "hide-announcement" });
        }
    });

    // Rules changes: when the leader updates rules, re-evaluate local tab.
    rulesCache.onChange(() => {
        if (!leaderElection.getIsLeader()) return;
        // A fresh evaluation request fires on the next url-change or registration.
        urlEvaluator.clear();
    });

    // Device status: no action beyond caching for now. Hook lives here so the
    // placeholder listener does not drift into silent inconsistency if later
    // phases need to react (e.g., browser-lock overlay).
    deviceStatusCache.onChange(() => {});

    leaderElection.onChange(async (isLeader) => {
        setStatus(isLeader ? "leader" : "follower");
        if (isLeader) {
            try {
                await Container.get(AgentConnector).connect();
            } catch (err) {
                console.error("[controller] AgentConnector failed", err);
            }
        } else {
            Container.get(AgentConnector).disconnect();
        }
    });

    await contentScriptChannel.waitUntilReady().catch(() => {
        /* waitUntilReady never rejects today, but defensive in case it changes */
    });

    setStatus("ready");
}

bootstrap().catch((err) => {
    console.error("[zaindari-controller] bootstrap failed", err);
    setStatus("error");
});
